import { useRef, useState } from "react";
import { toPng } from "html-to-image";
import { toast } from "sonner";
import { Modal } from "./Layout";

export function ShareCard({ card, onDone }) {
  const ref = useRef(null);
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  if (!card) return null;

  const doShare = async () => {
    setBusy(true);
    try {
      const dataUrl = await toPng(ref.current, { pixelRatio: 2, cacheBust: true });
      const blob = await (await fetch(dataUrl)).blob();
      const file = new File([blob], "bouquet-tile.png", { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], text: message.trim() || undefined });
      } else {
        const a = document.createElement("a");
        a.href = dataUrl;
        a.download = "bouquet-tile.png";
        a.click();
        if (message.trim()) {
          await navigator.clipboard.writeText(message.trim());
          toast.success("Image saved — your message is copied to paste with it");
        } else {
          toast.success("Image saved — attach it in your chat");
        }
      }
    } catch {
      if (message.trim()) {
        try {
          await navigator.clipboard.writeText(message.trim());
          toast.success("Copied as text — paste it in your chat");
        } catch { /* no clipboard */ }
      } else {
        toast.error("Couldn't create the image");
      }
    } finally {
      setBusy(false);
      onDone();
    }
  };

  return (
    <>
      <Modal onClose={onDone} testid="share-compose-modal">
        <div className="space-y-4">
          <h3 className="font-display text-xl font-medium">Share this tile</h3>
          <div className="border border-border rounded-xl p-4 bg-background space-y-1" data-testid="share-card-preview">
            {card.eyebrow && <p className="text-[10px] uppercase tracking-widest font-semibold text-muted-foreground">{card.eyebrow}</p>}
            <p className="font-display text-lg font-medium leading-snug">{card.title}</p>
            {card.body && <p className="text-xs text-muted-foreground line-clamp-2">{card.body}</p>}
            {card.footer && <p className="text-[11px] text-muted-foreground pt-1 border-t border-border">{card.footer}</p>}
          </div>
          <textarea data-testid="share-message-input" value={message} onChange={(e) => setMessage(e.target.value)}
            rows={3} placeholder="Add your own message (optional)…"
            className="w-full px-3.5 py-2.5 rounded-lg border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring" />
          <button data-testid="share-image-submit" onClick={doShare} disabled={busy}
            className="w-full bg-primary text-primary-foreground py-3 rounded-full text-sm font-semibold hover:opacity-90 transition-opacity disabled:opacity-50">
            {busy ? "Preparing image…" : "Share image"}
          </button>
        </div>
      </Modal>
      <div style={{ position: "fixed", left: -9999, top: 0 }} aria-hidden="true">
        <div ref={ref} className="w-[480px] bg-background rounded-2xl border border-border p-7 space-y-4" data-testid="share-card">
          <img src="/logo-trim.png" alt="Bouquet" className="h-9 object-contain" />
          {card.image && (
            <img src={card.image} alt="" className="w-full h-44 object-cover rounded-xl" />
          )}
          {card.eyebrow && <p className="text-xs font-semibold tracking-widest uppercase text-muted-foreground">{card.eyebrow}</p>}
          <p className="font-display text-2xl font-medium leading-snug">{card.title}</p>
          {card.body && <p className="text-sm text-muted-foreground leading-relaxed">{card.body}</p>}
          {card.footer && <p className="text-xs text-muted-foreground pt-2 border-t border-border">{card.footer}</p>}
        </div>
      </div>
    </>
  );
}
